import { TrackedEvent } from '@enmon/commons/lib/enmon';
import { captureException } from '@sentry/react';
import { useServices } from '@/components/Services';
import { type UseMutationResult, useMutation } from 'react-query';
import type { DownloadExportArgs, DownloadExportResult } from '@/services/api/exports';
import { config } from '@/config';
import { isNil } from 'ramda';
import { useAppDispatch } from '@/hooks';
import { actionFailed } from '@/containers/App/actions';
import { MUTATION_KEYS } from '@/utils/mutationKeys';
import { trackEvent } from '@/hooks/google_analytics/trackEvent';

export const useDownloadExport = (): UseMutationResult<DownloadExportResult, unknown, DownloadExportArgs> => {
  const { exportsApiClient } = useServices();
  const dispatch = useAppDispatch();

  return useMutation({
    mutationKey: MUTATION_KEYS.exports.download(),
    mutationFn: async (args: DownloadExportArgs) => await exportsApiClient.download(args),
    onSuccess: (responseData, { exportId, exportMessage }) => {
      if (isNil(responseData)) {
        dispatch(actionFailed());

        return;
      }

      const { accessToken } = responseData;

      if (accessToken) {
        trackEvent(TrackedEvent.ExportDownloaded);
        window.open(
          `${config.apiBaseUrl}api/exports/${exportId}/download?token=${accessToken}${
            exportMessage ? `&exportMessage=${exportMessage}` : ''
          }`,
        );
      }
    },
    onError: error => {
      console.error(error);
      captureException(error);
      dispatch(actionFailed());
    },
  });
};
